// Per-frame geometry of enhanced multi-frame objects (Enhanced CT / MR / XA…): Shared (5200,9229) and Per-frame
// (5200,9230) Functional Groups.
//
// cornerstone-wado-image-loader 4.13.2 merges these for wadors frames (combineFrameInstance) but reads the Shared
// groups as `it[0]` instead of `it.Value[0]`, so every Shared group is dropped. Enhanced CT/MR put Pixel Measures
// and Plane Orientation in the Shared groups, so frames lose their pixel spacing and orientation: measurements
// fall back to pixels and MPR/reference lines get no geometry. These functions read both groups properly (a
// Per-frame value wins over the Shared one, which wins over a top-level attribute).
//
// Pure functions (no Cornerstone, no DOM) so they can be unit-tested.

import type { DicomJson } from './dicomweb';

export const FG = {
  shared: '52009229',
  perFrame: '52009230',
  pixelMeasures: '00289110',
  planePosition: '00209113',
  planeOrientation: '00209116',
  frameVoiLut: '00289132',
} as const;

const TAG = {
  pixelSpacing: '00280030',
  sliceThickness: '00180050',
  position: '00200032',
  orientation: '00200037',
  windowCenter: '00281050',
  windowWidth: '00281051',
} as const;

export interface FramePlane {
  imagePositionPatient?: number[];
  imageOrientationPatient?: number[];
  rowCosines?: number[];
  columnCosines?: number[];
  rowPixelSpacing?: number;
  columnPixelSpacing?: number;
  sliceThickness?: number;
}

export interface FrameVoi {
  windowCenter: number[];
  windowWidth: number[];
}

function item(element: DicomJson[string]): DicomJson | undefined {
  const first = element?.Value?.[0];
  return first && typeof first === 'object' ? (first as DicomJson) : undefined;
}

function numbers(element: DicomJson[string], count: number): number[] | undefined {
  const values = (element?.Value ?? []).map(Number);
  return values.length >= count && values.slice(0, count).every(Number.isFinite) ? values.slice(0, count) : undefined;
}

/** True when the instance carries functional groups (an enhanced multi-frame object). */
export function isEnhancedMultiframe(json: DicomJson | undefined): boolean {
  return Boolean(json?.[FG.perFrame]?.Value?.length || json?.[FG.shared]?.Value?.length);
}

/** The sources of one frame's attribute, most specific first: per-frame group, shared group, top level. */
function sources(json: DicomJson, frame: number, group: string): DicomJson[] {
  const perFrameItem = item({ vr: 'SQ', Value: [json[FG.perFrame]?.Value?.[frame - 1]] });
  const sharedItem = item(json[FG.shared]);
  return [item(perFrameItem?.[group]), item(sharedItem?.[group]), json].filter((x): x is DicomJson => x !== undefined);
}

function pick(json: DicomJson, frame: number, group: string, tag: string, count: number): number[] | undefined {
  for (const source of sources(json, frame, group)) {
    const found = numbers(source[tag], count);
    if (found) return found;
  }
  return undefined;
}

/** Geometry of ``frame`` (1-based) of an enhanced multi-frame instance; undefined for other objects. */
export function framePlane(json: DicomJson | undefined, frame: number): FramePlane | undefined {
  if (!json || !isEnhancedMultiframe(json) || !Number.isInteger(frame) || frame < 1) return undefined;
  const spacing = pick(json, frame, FG.pixelMeasures, TAG.pixelSpacing, 2);
  const orientation = pick(json, frame, FG.planeOrientation, TAG.orientation, 6);
  return {
    imagePositionPatient: pick(json, frame, FG.planePosition, TAG.position, 3),
    imageOrientationPatient: orientation,
    rowCosines: orientation?.slice(0, 3),
    columnCosines: orientation?.slice(3, 6),
    rowPixelSpacing: spacing?.[0],
    columnPixelSpacing: spacing?.[1],
    sliceThickness: pick(json, frame, FG.pixelMeasures, TAG.sliceThickness, 1)?.[0],
  };
}

/** Window centre/width of ``frame`` from the Frame VOI LUT group (per-frame, else shared); undefined if none. */
export function frameVoi(json: DicomJson | undefined, frame: number): FrameVoi | undefined {
  if (!json || !isEnhancedMultiframe(json)) return undefined;
  const center = pick(json, frame, FG.frameVoiLut, TAG.windowCenter, 1);
  const width = pick(json, frame, FG.frameVoiLut, TAG.windowWidth, 1);
  return center && width ? { windowCenter: center, windowWidth: width } : undefined;
}
